import { Link } from "@tanstack/react-router";
import { FileSignature } from "lucide-react";
import type { ReactNode } from "react";

const NAV = [{ to: "/implementation", label: "Implementation" }] as const;

/** Public marketing chrome: landing page, pricing pages. */
export function SiteShell({ children }: { children: ReactNode }) {
  return (
    <div className="relative min-h-dvh">
      <header
        className="sticky top-0 z-40 border-b border-[var(--glass-border)] bg-background/95 backdrop-blur-xl backdrop-saturate-150 supports-[backdrop-filter]:bg-background/85"
        role="banner"
      >
        <div className="mx-auto flex items-center gap-3 h-14 sm:h-16 max-w-7xl px-3 sm:px-6">
          <Link
            to="/"
            aria-label="ArosFlow — go to home"
            className="focus-key flex min-w-0 items-center gap-2 sm:gap-3 rounded-md"
          >
            <div
              aria-hidden="true"
              className="relative h-8 w-8 sm:h-9 sm:w-9 shrink-0 rounded-md gradient-gold shadow-gold flex items-center justify-center"
            >
              <FileSignature className="h-4 w-4 sm:h-5 sm:w-5 text-black" strokeWidth={2.25} />
            </div>
            <div className="leading-tight min-w-0">
              <div className="display text-base sm:text-lg tracking-[0.16em] sm:tracking-[0.18em] text-foreground truncate">
                AROS <span className="text-gold">FLOW</span>
              </div>
              <div className="text-[8px] sm:text-[10px] uppercase tracking-[0.2em] sm:tracking-[0.3em] text-muted-foreground -mt-0.5 truncate">
                AI Real Estate Deal Operating System
              </div>
            </div>
          </Link>

          <nav aria-label="Main" className="ml-auto hidden md:flex items-center gap-1">
            {NAV.map((n) => (
              <Link
                key={n.to}
                to={n.to}
                className="focus-key rounded-md px-3 py-1.5 text-xs text-white/75 hover:bg-white/5 hover:text-gold"
                activeProps={{ className: "text-gold" }}
              >
                {n.label}
              </Link>
            ))}
          </nav>

          <Link
            to="/implementation"
            hash="request"
            className="focus-key ml-auto whitespace-nowrap rounded-md gradient-gold px-3 py-2 text-xs font-semibold text-black md:ml-3"
          >
            Request a call
          </Link>
        </div>
      </header>

      <main className="relative" id="main-content">
        {children}
      </main>

      <footer className="border-t border-[var(--glass-border)] py-10 mt-20" role="contentinfo">
        <div className="mx-auto flex max-w-7xl flex-col gap-6 px-6 text-xs text-muted-foreground">
          <div className="flex flex-col gap-5 sm:flex-row sm:justify-between">
            <div className="display tracking-[0.25em]">AROS FLOW · DEAL OPERATING SYSTEM</div>
            <nav aria-label="Product" className="flex flex-wrap gap-4">
              <Link to="/implementation" className="focus-key hover:text-gold">
                Implementation
              </Link>
            </nav>
          </div>
          <div className="flex flex-col gap-4 border-t border-[var(--glass-border)] pt-5 sm:flex-row sm:items-center sm:justify-between">
            <nav aria-label="Legal" className="flex flex-wrap gap-4">
              <Link to="/legal/privacy" className="focus-key hover:text-gold">
                Privacy Policy
              </Link>
              <Link to="/legal/terms" className="focus-key hover:text-gold">
                Terms of Service
              </Link>
              <Link to="/legal/refunds" className="focus-key hover:text-gold">
                Refund &amp; Cancellation
              </Link>
              <a href="mailto:support@arosflow.com" className="focus-key hover:text-gold">
                Contact
              </a>
            </nav>
            <div>© {new Date().getFullYear()} · Estimates only, not financial advice.</div>
          </div>
        </div>
      </footer>
    </div>
  );
}
